import { useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Heading, Modal } from '@zillow/constellation-10';

import PropertiesActions from '../../../actions/PropertiesActions';
import PostingActions from '../../../actions/PostingActions';
import { PropertiesSelectors } from '../../../selectors';

import DispatchConstants from '../../../constants/DispatchConstants';
import PropertyConstants from '../../../constants/PropertyConstants';

import ModalFooter from '../ModalFooter';

import Confirmation from './components/Confirmation';
import ConfirmationHasError from './components/ConfirmationHasError';
import ConfirmationThanks from './components/ConfirmationThanks';
import ScheduleDeactivation from './components/ScheduleDeactivation';

import localReducer, { ACTION, INIT_STATE } from './reducer';

import { RADIO_OPTIONS, STEPS } from './constants';

const { SYNDICATION_OVERVIEW_STATUS, LISTING_STATUS } = PropertyConstants;

const propTypes = {
  setShowFeedListingDeactivateModal: PropTypes.func.isRequired,
  launchedFrom: PropTypes.string.isRequired,
};
const defaultProps = {};

const FeedListingDeactivateModal = ({ setShowFeedListingDeactivateModal }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { listingAlias } = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));
  const [selectedReasonKey, setSelectedReasonKey] = useState();
  const [listingIssueReason, setListingIssueReason] = useState(null);
  const [step, setStep] = useState(STEPS.SCHEDULE_DEACTIVATION);

  const [{ errorMessage, hasError }, reducerDispatch] = useReducer(localReducer, INIT_STATE);

  const isCTADisabled = step === STEPS.SCHEDULE_DEACTIVATION && !selectedReasonKey;
  const heading =
    step === STEPS.SCHEDULE_DEACTIVATION ? 'Deactivate this listing?' : RADIO_OPTIONS[selectedReasonKey].modalHeader;

  const handleDeactivateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['getListingSummary', listingAlias] });
    queryClient.invalidateQueries({ queryKey: 'getProperties', refetchType: 'all' });

    const dataToUpdate = {
      active: false,
      status: 'OFF-MARKET',
      expirationDateTimestamp: Date.now(),
      syndication: {
        overview: {
          status: SYNDICATION_OVERVIEW_STATUS.INACTIVE,
          displayStatus: LISTING_STATUS.INACTIVE,
          publishedCount: 0,
        },
      },
    };

    dispatch(PropertiesActions.updateCurrentProperty(dataToUpdate));

    dispatch({
      type: DispatchConstants.UPDATE_PROPERTY_OVERVIEW,
      payload: dataToUpdate,
    });

    setStep(RADIO_OPTIONS[selectedReasonKey].step);
  };

  const handleDeactivateError = (errorRMWModel) => {
    reducerDispatch({
      type: ACTION.DISPLAY_ERROR,
      payload: {
        errorMessage: errorRMWModel.message,
      },
    });
  };

  const handleDeactivateClick = async () => {
    const { reason } = RADIO_OPTIONS[selectedReasonKey];
    const isListingIssueReasonSelectedWithComment = reason === RADIO_OPTIONS.HAS_ERROR.reason && listingIssueReason;
    const comment = isListingIssueReasonSelectedWithComment ? listingIssueReason : null;

    dispatch(PostingActions.property.deactivate(listingAlias, reason, comment))
      .then(handleDeactivateSuccess)
      .catch(handleDeactivateError);
  };

  return (
    <Modal
      header={<Heading level={2}>{heading}</Heading>}
      body={
        <>
          {step === STEPS.SCHEDULE_DEACTIVATION && (
            <ScheduleDeactivation
              setSelectedReasonKey={setSelectedReasonKey}
              selectedReasonKey={selectedReasonKey}
              listingIssueReason={listingIssueReason}
              setListingIssueReason={setListingIssueReason}
            />
          )}
          {step === STEPS.CONFIRMATION_THANKS && <ConfirmationThanks />}
          {(step === STEPS.CONFIRMATION || step === STEPS.OTHER) && <Confirmation />}
          {step === STEPS.CONFIRMATION_HAS_ERROR && <ConfirmationHasError />}
        </>
      }
      footer={
        step === STEPS.SCHEDULE_DEACTIVATION && (
          <ModalFooter
            submitText="Yes, deactivate listing"
            onSubmit={handleDeactivateClick}
            onCancel={() => setShowFeedListingDeactivateModal(false)}
            hasError={hasError}
            errorMessage={errorMessage}
            isSubmitDisabled={isCTADisabled}
          />
        )
      }
      onOpenChange={(open) => !open && setShowFeedListingDeactivateModal(false)}
      open={true}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

FeedListingDeactivateModal.propTypes = propTypes;
FeedListingDeactivateModal.defaultProps = defaultProps;

export default FeedListingDeactivateModal;
